import {
  getWidgetService,
  type ReadableWidgetService,
  type TextToSpeechWidgetData,
  IWidgetComponentApi,
} from '@playbooks/action-cards-utils'
import type { FunctionComponent } from 'react'
import { useRef, useState } from 'react'

import { Play, Pause, Speaker } from '../../components/ui'
import { htmlToPlainText } from '../../utils/htmlToPlainText'
import { type WidgetProps } from './widgetTypes'
import { usePlaybooksBackendClient } from '../../hooks/usePlaybooksBackendClient'
import useComponentsApiRegistry from '../../hooks/useComponentsApiRegistry'
import { useTranslation } from '../../contexts/localization/Localization'
import { CustomButton } from '../../components/ui/CustomButton'

interface TextToSpeechWidgetProps extends WidgetProps {
  /** Widget data */
  widget: TextToSpeechWidgetData
}

/**
 * Widget for reading Recommendation text aloud
 * @story User Story 8289: Action Card Recommendations: Text-to-Speech action
 */
const TextToSpeechWidget: FunctionComponent<TextToSpeechWidgetProps> = ({ widget, context }) => {
  // Refs
  const audio = useRef<HTMLAudioElement | null>(null)

  // State
  const [isLoading, setIsLoading] = useState(false)
  const [isActive, setIsActive] = useState(false) // Active means "audio is available and not yet finished playing" -→ show play/pause buttons
  const [isPlaying, setIsPlaying] = useState(false)

  // Custom hooks
  const playbooksBackendClient = usePlaybooksBackendClient()
  const { text: t } = useTranslation()

  useComponentsApiRegistry(widget.id, () => {
    return {
      activate: handleStartButtonClick,
    } as IWidgetComponentApi
  })

  /**
   * Handle the button click event: convert text to audio and start streaming it.
   */
  async function handleStartButtonClick() {
    if (isLoading) {
      return
    }
    if (!audio.current) return

    // Get the text to read aloud
    let textToRead = [
      // Include the recommendation text
      context?.visibleText,
      // Include the text generated by the AI widgets
      ...(context?.allWidgets.map(w => (getWidgetService(w) as ReadableWidgetService)?.getReadableText?.()) || []),
    ]
      .filter(str => str)
      .join(' ')

    // Convert HTML to plain text
    textToRead = htmlToPlainText(textToRead)
    if (!textToRead) {
      console.error('No text provided to read aloud.')
      return
    }
    console.log(`Reading aloud: ${textToRead}`)

    // Reset the state
    setIsLoading(true)
    setIsActive(false)
    setIsPlaying(false)

    // Reset the audio player
    audio.current.pause()
    audio.current.src = ''

    try {
      const url = await playbooksBackendClient.generateSpeech({ text: textToRead })

      // We need to handle the playback in two steps because some sites can throw CSP errors
      // (like "Refused to load the audio because it violates the following Content Security Policy directive: "media-src 'self'")
      // In the first step we try to play the audio directly from the URL,
      // if it fails we fetch the audio as a blob and play it from the blob URL.
      const playFromUrl = (src: string): Promise<void> =>
        new Promise((resolve, reject) => {
          const el = audio.current!
          // Set the audio source and attempt to play it
          el.src = src
          el.load()
          const onError = () => {
            el.removeEventListener('error', onError)
            reject(el.error)
          }
          el.addEventListener('error', onError, { once: true })
          el.play()
            .then(() => {
              el.removeEventListener('error', onError)
              resolve()
            })
            .catch(err => {
              el.removeEventListener('error', onError)
              reject(err)
            })
        })

      try {
        await playFromUrl(url)
      } catch (cspErr) {
        console.warn('Direct audio playback failed, falling back to blob:', cspErr)
        let blobUrl: string | null = null
        try {
          // Fetch the audio as a blob and play it from a blob URL
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`)
          }
          const blob = await response.blob()
          blobUrl = URL.createObjectURL(blob)
          await playFromUrl(blobUrl)
        } catch (blobErr) {
          if (blobUrl) URL.revokeObjectURL(blobUrl)
          throw blobErr // Re-throw for outer catch to handle
        }
      }

      // Update UI state on successful playback
      setIsLoading(false)
      setIsActive(true)
      setIsPlaying(true)
    } catch (err) {
      // Handle streaming errors
      console.error('Playback error:', err)
      setIsLoading(false)
      setIsActive(false)
    }
  }

  return (
    <div className="flex">
      {/* Button to start streaming the audio */}
      <CustomButton
        size="lg"
        isLoading={isLoading}
        onClick={handleStartButtonClick}
        tooltip={widget.buttonLabel || t('startTextToSpeech')}
      >
        <>{widget.buttonLabel || <Speaker className="w-4 h-4" />}</>
      </CustomButton>

      {/* Play button */}
      {isActive && !isPlaying && (
        <CustomButton
          size="lg"
          onClick={() => {
            audio.current && audio.current.play()
            setIsPlaying(true)
          }}
          className="ml-2"
          tooltip={t('playTextToSpeech')}
        >
          <Play className="w-4 h-4" />
        </CustomButton>
      )}

      {/* Pause button */}
      {isActive && isPlaying && (
        <CustomButton
          size="lg"
          onClick={async () => {
            audio.current && audio.current.pause()
            setIsPlaying(false)
          }}
          className="ml-2"
          tooltip={t('pauseTextToSpeech')}
        >
          <Pause className="w-4 h-4" />
        </CustomButton>
      )}

      {/* Audio player (hidden) */}
      <audio
        controls
        ref={audio}
        className="hidden"
        onEnded={() => {
          setIsPlaying(false)
          setIsActive(false)
          if (audio.current?.src.startsWith('blob:')) {
            URL.revokeObjectURL(audio.current.src)
          }
        }}
      ></audio>
    </div>
  )
}

export default TextToSpeechWidget
